import React, { useState } from "react";
import { Form, InputGroup, FormControl, Button, ButtonGroup, Row, Col } from "react-bootstrap";
import ColorPicker from "../../../lib/plugins/color-picker/ColorPicker";
import { SHAPE_BORDER_STYLES } from "../../../lib/global/helpers";
import { PAGE_FRAME_DEFAULTS, getPageFrame, getPageFrameSide } from "../../../lib/bands/bandModel";

const SIDE_OPTIONS = [
  { value: "all", label: "All" },
  { value: "top", label: "Top" },
  { value: "right", label: "Right" },
  { value: "bottom", label: "Bottom" },
  { value: "left", label: "Left" },
];

// Page frame field for each side setting.
const FRAME_FIELD = { color: "borderColor", width: "borderWidth", style: "borderStyle" };

const PxField = ({ id, label, value, onChange }) => (
  <Form.Group controlId={id}>
    <Form.Label className="small mb-1">{label}</Form.Label>
    <InputGroup size="sm" style={{ flexWrap: "nowrap" }}>
      <FormControl
        type="number"
        min="0"
        value={value}
        onChange={(e) => {
          const n = parseFloat(e.target.value);
          onChange(n >= 0 ? n : 0);
        }}
      />
      <InputGroup.Text>px</InputGroup.Text>
    </InputGroup>
  </Form.Group>
);

// "Page border" at the top of the Properties tab: a border inside the margins
// on every page with the same options as a shape's border — all sides
// together or each side on its own. `onChange(null)` removes it.
export default function PageBorderSettings({ pageFrame, onChange }) {
  const [side, setSide] = useState("all");
  const frame = getPageFrame({ pageFrame }) || { ...PAGE_FRAME_DEFAULTS, show: false };
  const update = (patch) => onChange({ ...(pageFrame || PAGE_FRAME_DEFAULTS), ...patch, show: true });

  // What the fields show: the all-sides values, or one side's after overrides.
  const current = side === "all"
    ? { color: frame.borderColor, width: frame.borderWidth, style: frame.borderStyle }
    : getPageFrameSide(frame, side);
  const sides = frame.borderSides || {};
  const hasOwn = (s) => Object.keys(sides[s] || {}).length > 0;
  const sideName = side === "all" ? "" : ` (${side})`;

  // Same as a shape: "All" sets every side and drops that setting's per-side
  // overrides; one side keeps its own value.
  const setBorder = (field, value) => {
    if (side === "all") {
      const borderSides = {};
      Object.keys(sides).forEach((s) => {
        const { [field]: _removed, ...rest } = sides[s] || {};
        if (Object.keys(rest).length) borderSides[s] = rest;
      });
      update({ [FRAME_FIELD[field]]: value, borderSides });
    } else {
      update({ borderSides: { ...sides, [side]: { ...(sides[side] || {}), [field]: value } } });
    }
  };

  const resetSide = () => {
    const { [side]: _removed, ...rest } = sides;
    update({ borderSides: rest });
  };

  return (
    <div className="page-border-settings border-bottom pb-3 mb-3">
      <div className="form-check">
        <input
          className="form-check-input"
          type="checkbox"
          id="forPageFrame"
          checked={frame.show}
          onChange={(e) => (e.target.checked ? update({}) : onChange(null))}
        />
        <label className="form-check-label fw-bold" htmlFor="forPageFrame">
          Page border
        </label>
      </div>

      {frame.show && (
        <div className="mt-2 p-2 border rounded bg-light">
          <Form.Text className="text-muted d-block mb-2">
            Drawn inside the margins on every page; the sections sit inside it.
          </Form.Text>

          <ButtonGroup size="sm" className="d-flex mb-2" role="radiogroup" aria-label="Border side">
            {SIDE_OPTIONS.map((opt) => (
              <Button
                key={opt.value}
                className="flex-fill px-1"
                variant={side === opt.value ? "primary" : "outline-secondary"}
                role="radio"
                aria-checked={side === opt.value}
                title={opt.value === "all" ? "Edit all sides together" : `Edit the ${opt.label.toLowerCase()} border only`}
                onClick={() => setSide(opt.value)}
              >
                {opt.label}
                {opt.value !== "all" && hasOwn(opt.value) && <span aria-hidden="true"> •</span>}
              </Button>
            ))}
          </ButtonGroup>

          <Form.Group className="compact-colour-field mb-2">
            <Form.Label className="small mb-1">Colour{sideName}</Form.Label>
            <ColorPicker color={current.color} callbackFromParents={(e) => setBorder("color", e.hex)} />
          </Form.Group>

          <Row className="g-2 mb-1">
            <Col xs={6}>
              <PxField id="forPageFrameHeight" label={`Height${sideName}`} value={current.width} onChange={(v) => setBorder("width", v)} />
            </Col>
            <Col xs={6}>
              <Form.Group controlId="forPageFrameStyle">
                <Form.Label className="small mb-1">Style{sideName}</Form.Label>
                <select className="form-select form-select-sm" value={current.style} onChange={(e) => setBorder("style", e.target.value)}>
                  {SHAPE_BORDER_STYLES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Form.Group>
            </Col>
          </Row>

          <Form.Text className="text-muted d-block">
            {side === "all" && "Height 0 hides a side. • marks sides with their own settings."}
            {side !== "all" && !hasOwn(side) && "Changes here apply to this side only."}
            {side !== "all" && hasOwn(side) && (
              <>
                This side has its own settings.{" "}
                <Button variant="link" size="sm" className="p-0 align-baseline" onClick={resetSide}>Reset to all sides</Button>
              </>
            )}
          </Form.Text>

          <hr className="my-2" />

          <Row className="g-2">
            <Col xs={6}>
              <PxField id="forPageFrameRadius" label="Corner radius" value={frame.borderRadius} onChange={(v) => update({ borderRadius: v })} />
            </Col>
            <Col xs={6}>
              <PxField id="forPageFramePadding" label="Inner padding" value={frame.padding} onChange={(v) => update({ padding: v })} />
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
}
